<template>
  <footer
    :class="[
      'main-footer text-white pb-[max(1.25rem,env(safe-area-inset-bottom))]',
      footerColor,
    ]"
  >
    <div
      class="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-11 lg:py-14"
    >
      <!-- Mobile: brand full width, then About | Support in two columns; md+: 4-column row -->
      <div
        class="grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-10 sm:gap-x-8 md:gap-x-6 lg:gap-10"
      >
        <!-- Logo and Description -->
        <div
          class="col-span-2 md:col-span-2 text-center md:text-left rounded-2xl md:rounded-none bg-white/[0.03] md:bg-transparent ring-1 ring-white/10 md:ring-0 p-5 sm:p-6 md:p-0"
        >
          <h3
            class="logo footer-logo w-fit text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 mx-auto md:mx-0 tracking-tight"
          >
            M-GATE
          </h3>
          <p
            class="text-sm sm:text-[0.9375rem] leading-relaxed text-gray-400 mb-5 max-w-md mx-auto md:mx-0"
          >
            {{ $t('footer.tagline') }}
          </p>
          <div
            class="inline-flex justify-center md:justify-start rounded-2xl bg-black/20 p-1 ring-1 ring-white/10"
          >
            <a
              href="#"
              class="social-icon inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-xl text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/15 transition-colors duration-200 touch-manipulation"
              :aria-label="$t('footer.social_facebook_aria')"
            >
              <i class="fab fa-facebook text-lg sm:text-xl" />
            </a>
            <a
              href="#"
              class="social-icon inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-xl text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/15 transition-colors duration-200 touch-manipulation"
              :aria-label="$t('footer.social_twitter_aria')"
            >
              <i class="fab fa-twitter text-lg sm:text-xl" />
            </a>
            <a
              href="#"
              class="social-icon inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-xl text-gray-400 hover:text-white hover:bg-white/10 active:bg-white/15 transition-colors duration-200 touch-manipulation"
              :aria-label="$t('footer.social_telegram_aria')"
            >
              <i class="fab fa-telegram text-lg sm:text-xl" />
            </a>
          </div>
        </div>

        <!-- About Us -->
        <div class="footer-col min-w-0 text-left md:text-left">
          <h4
            class="text-[0.6875rem] sm:text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-3 sm:mb-4"
          >
            {{ $t('footer.about_us') }}
          </h4>
          <ul class="space-y-0.5 sm:space-y-1">
            <li>
              <RouterLink
                to="/about/project"
                class="footer-link -mx-1.5 px-1.5"
                @click="scrollToTop"
              >
                {{ $t('footer.project') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                to="/about/team"
                class="footer-link -mx-1.5 px-1.5"
                @click="scrollToTop"
              >
                {{ $t('footer.team') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                to="/about/recruitment"
                class="footer-link -mx-1.5 px-1.5"
                @click="scrollToTop"
              >
                {{ $t('footer.recruitment') }}
              </RouterLink>
            </li>
          </ul>
        </div>

        <!-- Support -->
        <div class="footer-col min-w-0 text-left md:text-left">
          <h4
            class="text-[0.6875rem] sm:text-xs font-semibold uppercase tracking-[0.12em] text-gray-500 mb-3 sm:mb-4"
          >
            {{ $t('footer.support') }}
          </h4>
          <ul class="space-y-0.5 sm:space-y-1">
            <li>
              <RouterLink
                to="/about/faq"
                class="footer-link -mx-1.5 px-1.5"
                @click="scrollToTop"
              >
                {{ $t('footer.faq') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                to="/about/terms"
                class="footer-link -mx-1.5 px-1.5"
                @click="scrollToTop"
              >
                {{ $t('footer.terms') }}
              </RouterLink>
            </li>
            <li>
              <RouterLink
                to="/about/privacy"
                class="footer-link -mx-1.5 px-1.5"
                @click="scrollToTop"
              >
                {{ $t('footer.privacy') }}
              </RouterLink>
            </li>
          </ul>
        </div>
      </div>

      <div
        class="relative mt-10 sm:mt-12 pt-8 sm:pt-10 text-center before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-white/15 before:to-transparent"
      >
        <p
          class="text-[0.6875rem] sm:text-xs text-gray-500 leading-relaxed px-2 max-w-xl mx-auto"
        >
          {{ $t('footer.copyright') }}
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { scrollToTop } from '@/utils/commonUtils';

// Footer uses $t() from vue-i18n (injected in template)
interface Props {
  footerColor?: string
}
withDefaults(defineProps<Props>(), {
  footerColor: 'bg-gradient-to-b from-black/0 from-30% via-black/30 via-70% to-black'
})
</script>